import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { format } from 'date-fns';
import * as yup from 'yup';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { ModalType } from '@constants/modal';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { handleApiError } from '@helpers/api';
import { CouponStatus, CouponType } from '@constants/coupons';
import { DEFAULT_CURRENCY_BY_LOCALE } from '@constants/currencies';
import { couponsService } from '@services';
import { alertActions } from '@actions';
import PreviewCoupon from '../PreviewCoupon';
import OfferSchedule from './OfferSchedule';
import PrimaryFields from './PrimaryFields';
import Settings from './Settings';
import OfferType from './OfferType';
import { Modal, Content, FormWrapper, PreviewWrapper } from './styles';

const EditModal = ({ selectedCoupon, setSelectedCoupon, modal, setModal, brands, onSaved }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { locationsMap } = useLocations();
    const account = useSelector(state => state.account?.account);
    const defaultCurrency = DEFAULT_CURRENCY_BY_LOCALE[account?.locale] || 'USD';

    const CouponsSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        locationId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) =>
                locationType === LOCATION_TYPE.LOCATION || (locationType === LOCATION_TYPE.BRAND && !brandAccess),
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
        brandId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) => locationType === LOCATION_TYPE.BRAND && brandAccess,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
        offerEndDate: yup.string().when('offerEndEnabled', {
            is: offerEndEnabled => offerEndEnabled,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        waitPeriodInHours: yup.string().when('waitPeriodEnabled', {
            is: waitPeriodEnabled => waitPeriodEnabled,
            then: schema => schema.matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        redemptionPeriodInDays: yup.string().when('redemptionPeriodEnabled', {
            is: redemptionPeriodEnabled => redemptionPeriodEnabled,
            then: schema => schema.matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        pin: yup.string().when('pinEnabled', {
            is: pinEnabled => pinEnabled,
            then: schema => schema.min(4, 'validation.required').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        coverPhotoUrl: yup.string().when('coverPhotoEnabled', {
            is: coverPhotoEnabled => coverPhotoEnabled,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        percentageOff: yup.string().when('type', {
            is: type => type === CouponType.PercentageOff,
            then: schema => schema.matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        amountOff: yup.string().when('type', {
            is: type => type === CouponType.AmountOff,
            then: schema => schema.matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        freeItem: yup.string().when('type', {
            is: type => type === CouponType.FreeItem,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        minimumAmountCurrency: yup.string().required('validation.required'),
        defaultMessageEnabled: yup.boolean(),
        defaultMessage: yup.string().when('defaultMessageEnabled', {
            is: defaultMessageEnabled => defaultMessageEnabled,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(CouponsSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        clearErrors,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            locationId: '',
            brandId: '',
            locationType: LOCATION_TYPE.BRAND,
            brandAccess: false,
            status: Boolean(CouponStatus.Paused),
            offerEndEnabled: false,
            redemptionPeriodEnabled: false,
            waitPeriodEnabled: false,
            redemptionPeriodInDays: '',
            waitPeriodInHours: '',
            offerEndDate: '',
            pinEnabled: false,
            pin: '',
            coverPhotoEnabled: false,
            coverPhotoUrl: '',
            type: CouponType.PercentageOff,
            percentageOff: '',
            amountOff: '',
            freeItem: '',
            minimumAmountCurrency: defaultCurrency,
            minimumAmount: '',
            defaultMessageEnabled: false,
            defaultMessage: '',
        },
    });

    const defaultsApplied = useRef(false);

    useEffect(() => {
        if (modal.type !== ModalType.Create) return;
        if (defaultsApplied.current) return;
        if (brands.length > 0) {
            const defaultBrand = brands.find(b => b.isDefault) || brands[0];
            setValue('brandId', defaultBrand.id, { shouldValidate: true });
            defaultsApplied.current = true;
        } else if (Object.keys(locationsMap).length > 0) {
            const firstLocationId = Object.keys(locationsMap)[0];
            setValue('locationId', firstLocationId, { shouldValidate: true });
            setValue('locationType', LOCATION_TYPE.LOCATION, { shouldValidate: true });
            defaultsApplied.current = true;
        }
    }, [modal.type, brands, locationsMap, setValue]);

    useEffect(() => {
        if (modal.type !== ModalType.Create) {
            setValue('title', selectedCoupon.title, { shouldValidate: true });
            setValue('status', Boolean(selectedCoupon.status));
            setValue('offerEndEnabled', selectedCoupon.offerEndEnabled, { shouldValidate: true });
            setValue('redemptionPeriodEnabled', selectedCoupon.redemptionPeriodEnabled, { shouldValidate: true });
            setValue('waitPeriodEnabled', selectedCoupon.waitPeriodEnabled, { shouldValidate: true });
            setValue('redemptionPeriodInDays', selectedCoupon.redemptionPeriodInDays || '', { shouldValidate: true });
            setValue('waitPeriodInHours', selectedCoupon.waitPeriodInHours || '', { shouldValidate: true });
            setValue('offerEndDate', selectedCoupon.offerEndDate ? new Date(selectedCoupon.offerEndDate) : '', {
                shouldValidate: true,
            });
            setValue('pinEnabled', selectedCoupon.pinEnabled, { shouldValidate: true });
            setValue('pin', selectedCoupon.pin ? `${selectedCoupon.pin}` : '', { shouldValidate: true });
            setValue('coverPhotoEnabled', selectedCoupon.coverPhotoEnabled, { shouldValidate: true });
            setValue('coverPhotoUrl', selectedCoupon.coverPhotoUrl || '', { shouldValidate: true });
            setValue('type', selectedCoupon.type, { shouldValidate: true });
            setValue('percentageOff', selectedCoupon.percentageOff || '', { shouldValidate: true });
            setValue('amountOff', selectedCoupon.amountOff || '', { shouldValidate: true });
            setValue('freeItem', selectedCoupon.freeItem || '', { shouldValidate: true });
            setValue('minimumAmountCurrency', selectedCoupon.minimumAmountCurrency || defaultCurrency, {
                shouldValidate: true,
            });
            setValue('minimumAmount', selectedCoupon.minimumAmount || '', { shouldValidate: true });
            setValue('defaultMessageEnabled', !!selectedCoupon.defaultMessage, { shouldValidate: true });
            setValue('defaultMessage', selectedCoupon.defaultMessage || '', { shouldValidate: true });
        }
    }, [selectedCoupon, modal.type, setValue]);

    const onClose = () => {
        setSelectedCoupon(null);
        setModal({ open: false, type: ModalType.Create });
    };

    const RESPONSE_MSG = {
        [ModalType.Edit]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [ModalType.Create]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await couponsService.createOrUpdateCoupon({
                ...(modal.type === ModalType.Edit && { id: selectedCoupon?.id }),
                ...values,
                amountOff: values.amountOff ? values.amountOff : null,
                percentageOff: values.percentageOff ? values.percentageOff : null,
                minimumAmount: values.minimumAmount ? values.minimumAmount : null,
                status: values.status ? CouponStatus.Active : CouponStatus.Paused,
                offerEndDate: values.offerEndDate ? format(new Date(values.offerEndDate), 'yyyy-MM-dd') : '',
            });
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
            return;
        }

        onSaved?.();
        dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
        onClose();
    };

    const isView = modal.type === ModalType.View;

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={
                modal.type === ModalType.Create
                    ? t('CouponsPage.createCoupon')
                    : modal.type === ModalType.View
                      ? t('CouponsPage.viewCoupon')
                      : t('CouponsPage.editCoupon')
            }
            onPrimaryAction={modal.type === ModalType.View ? null : handleSubmit(onSubmit)}
            primaryActionText={modal.type === ModalType.Edit ? t('buttons.saveChanges') : t('CouponsPage.createCoupon')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
        >
            <FormProvider {...{ getValues, setValue, control, watch, clearErrors }}>
                <FormWrapper>
                    <Content>
                        <PrimaryFields isView={isView} selectedCoupon={selectedCoupon} brands={brands} />
                        <OfferType isView={isView} />
                        <OfferSchedule isView={isView} />
                        <Settings isView={isView} />
                    </Content>
                </FormWrapper>
                <PreviewWrapper>
                    <PreviewCoupon brands={brands} />
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
